import { useState, useCallback } from 'react'
import { AircraftOverride, TransactionOverride } from '../types/database'
import type { EditingCellFieldName, EditingCell } from '../components/market-table/types'
import { OverridesService } from '../services/overrides.service'
import { TransactionOverridesNewService } from '../services/transaction-overrides-new.service'
import { CompaniesService } from '../services/intellijetnet/companies.service'
import type { Company } from '../types/intellijetnet'

interface UseMarketTableEditingParams {
  setOverrides: React.Dispatch<React.SetStateAction<Map<number, AircraftOverride>>>
  setTransactionOverrides: React.Dispatch<React.SetStateAction<Map<string, TransactionOverride>>>
  setCompanyMap: React.Dispatch<React.SetStateAction<Map<number, Company>>>
  companyMap: Map<number, Company>
  triggerOptionsRefresh: (aircraftId: number) => void
}

export function useMarketTableEditing({
  setOverrides,
  setTransactionOverrides,
  setCompanyMap,
  companyMap,
  triggerOptionsRefresh,
}: UseMarketTableEditingParams) {
  // Inline editing state - tracks which cell is currently being edited
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)

  // Inline options editing state - tracks which aircraft's options are being edited
  const [editingOptionsAircraftId, setEditingOptionsAircraftId] = useState<number | null>(null)

  // Handler for saving take price with modifier (for ac_overrides - For Sale and Removed tables)
  const handleTakePriceSave = useCallback(async (
    aircraftId: number,
    priceFieldName: 'take_price' | 'external_take_price',
    newValue: number | null,
    newModifier: import('../types/database').TakePriceModifier | null
  ) => {
    try {
      const modifierFieldName = priceFieldName === 'take_price'
        ? 'take_price_modifier'
        : 'external_take_price_modifier'

      await OverridesService.upsert({
        aircraft_id: aircraftId,
        [priceFieldName]: newValue,
        [modifierFieldName]: newModifier
      })

      setOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(aircraftId) || { aircraft_id: aircraftId } as AircraftOverride
        newMap.set(aircraftId, {
          ...existing,
          [priceFieldName]: newValue,
          [modifierFieldName]: newModifier
        })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.aircraftId === aircraftId && prev?.fieldName === priceFieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setOverrides])

  // Handler for saving asking price with make_offer flag (for ac_overrides - For Sale and Removed tables)
  const handleAskPriceSave = useCallback(async (
    aircraftId: number,
    priceFieldName: 'asking_price' | 'external_asking_price',
    newValue: number | null,
    newMakeOffer: boolean | null
  ) => {
    try {
      const makeOfferFieldName = priceFieldName === 'asking_price'
        ? 'actual_ask_make_offer'
        : 'adj_ask_make_offer'

      await OverridesService.upsert({
        aircraft_id: aircraftId,
        [priceFieldName]: newValue,
        [makeOfferFieldName]: newMakeOffer
      })

      setOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(aircraftId) || { aircraft_id: aircraftId } as AircraftOverride
        newMap.set(aircraftId, {
          ...existing,
          [priceFieldName]: newValue,
          [makeOfferFieldName]: newMakeOffer
        })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.aircraftId === aircraftId && prev?.fieldName === priceFieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setOverrides])

  // Handler for saving inline text/dropdown edits (for ac_overrides - For Sale and Removed tables)
  const handleFieldSave = useCallback(async (
    aircraftId: number,
    fieldName: EditingCellFieldName,
    newValue: string | number | null
  ) => {
    try {
      await OverridesService.updateField(aircraftId, fieldName as keyof import('../types/database').AircraftOverrideInput, newValue, 'inline_edit')

      setOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(aircraftId) || { aircraft_id: aircraftId } as AircraftOverride
        newMap.set(aircraftId, { ...existing, [fieldName]: newValue })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.aircraftId === aircraftId && prev?.fieldName === fieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setOverrides])

  // Handler for saving owner/broker edits (involves two fields: company ID and custom text)
  const handleCompanySave = useCallback(async (
    aircraftId: number,
    fieldType: 'owner' | 'broker',
    companyId: number | null,
    customValue: string | null
  ) => {
    try {
      const companyField = fieldType
      const customField = `${fieldType}_custom` as 'owner_custom' | 'broker_custom'

      await OverridesService.upsert({
        aircraft_id: aircraftId,
        [companyField]: companyId,
        [customField]: customValue
      })

      setOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(aircraftId) || { aircraft_id: aircraftId } as AircraftOverride
        newMap.set(aircraftId, {
          ...existing,
          [companyField]: companyId,
          [customField]: customValue
        })
        return newMap
      })

      if (companyId && !companyMap.has(companyId)) {
        try {
          const company = await CompaniesService.getById(companyId)
          if (company) {
            setCompanyMap(prev => {
              const newMap = new Map(prev)
              newMap.set(companyId, company)
              return newMap
            })
          }
        } catch (err) {
          // Failed to fetch company
        }
      }

      setEditingCell(prev => {
        if (prev?.aircraftId === aircraftId && prev?.fieldName === fieldType) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [companyMap, setCompanyMap, setOverrides])

  // Handler for saving inline price edits for transaction overrides (Sold table)
  const handleTransactionPriceSave = useCallback(async (
    transactionId: string,
    fieldName: EditingCellFieldName,
    newValue: number | null
  ) => {
    try {
      await TransactionOverridesNewService.updateField(transactionId, fieldName as keyof import('../types/database').TransactionOverrideInput, newValue, 'inline_edit')

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, { ...existing, [fieldName]: newValue })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.transactionId === transactionId && prev?.fieldName === fieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setTransactionOverrides])

  // Handler for saving take price with modifier for transaction overrides (Sold table)
  const handleTransactionTakePriceSave = useCallback(async (
    transactionId: string,
    priceFieldName: 'take_price' | 'external_take_price',
    newValue: number | null,
    newModifier: import('../types/database').TakePriceModifier | null
  ) => {
    try {
      const modifierFieldName = priceFieldName === 'take_price'
        ? 'take_price_modifier'
        : 'external_take_price_modifier'

      await TransactionOverridesNewService.upsert({
        transaction_id: transactionId,
        [priceFieldName]: newValue,
        [modifierFieldName]: newModifier
      })

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, {
          ...existing,
          [priceFieldName]: newValue,
          [modifierFieldName]: newModifier
        })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.transactionId === transactionId && prev?.fieldName === priceFieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setTransactionOverrides])

  // Handler for saving asking price with make_offer for transaction overrides (Sold table)
  const handleTransactionAskPriceSave = useCallback(async (
    transactionId: string,
    priceFieldName: 'asking_price' | 'external_asking_price',
    newValue: number | null,
    newMakeOffer: boolean | null
  ) => {
    try {
      const makeOfferFieldName = priceFieldName === 'asking_price'
        ? 'actual_ask_make_offer'
        : 'adj_ask_make_offer'

      await TransactionOverridesNewService.upsert({
        transaction_id: transactionId,
        [priceFieldName]: newValue,
        [makeOfferFieldName]: newMakeOffer
      })

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, {
          ...existing,
          [priceFieldName]: newValue,
          [makeOfferFieldName]: newMakeOffer
        })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.transactionId === transactionId && prev?.fieldName === priceFieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setTransactionOverrides])

  // Handler for saving inline text/dropdown edits for transaction overrides (Sold table)
  const handleTransactionFieldSave = useCallback(async (
    transactionId: string,
    fieldName: EditingCellFieldName,
    newValue: string | number | null
  ) => {
    try {
      await TransactionOverridesNewService.updateField(transactionId, fieldName as keyof import('../types/database').TransactionOverrideInput, newValue, 'inline_edit')

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, { ...existing, [fieldName]: newValue })
        return newMap
      })

      setEditingCell(prev => {
        if (prev?.transactionId === transactionId && prev?.fieldName === fieldName) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [setTransactionOverrides])

  // Handler for saving owner/broker edits for transaction overrides (Sold table)
  const handleTransactionCompanySave = useCallback(async (
    transactionId: string,
    fieldType: 'owner' | 'broker',
    companyId: number | null,
    customValue: string | null
  ) => {
    try {
      const companyField = fieldType
      const customField = `${fieldType}_custom` as 'owner_custom' | 'broker_custom'

      await TransactionOverridesNewService.upsert({
        transaction_id: transactionId,
        [companyField]: companyId,
        [customField]: customValue
      })

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, { ...existing, [companyField]: companyId, [customField]: customValue })
        return newMap
      })

      if (companyId && !companyMap.has(companyId)) {
        try {
          const company = await CompaniesService.getById(companyId)
          if (company) {
            setCompanyMap(prev => new Map(prev).set(companyId, company))
          }
        } catch (err) {
          // Failed to fetch company for display
        }
      }

      setEditingCell(prev => {
        if (prev?.transactionId === transactionId && prev?.fieldName === fieldType) {
          return null
        }
        return prev
      })
    } catch (error) {
      throw error
    }
  }, [companyMap, setCompanyMap, setTransactionOverrides])

  // Handler for starting inline edit (for ac_overrides)
  const handleStartEdit = useCallback((aircraftId: number, fieldName: EditingCellFieldName) => {
    setEditingCell({ aircraftId, fieldName })
  }, [])

  // Handler for starting inline edit for transaction (Sold table)
  const handleStartTransactionEdit = useCallback((aircraftId: number, transactionId: string, fieldName: EditingCellFieldName) => {
    setEditingCell({ aircraftId, transactionId, fieldName })
  }, [])

  // Handler for canceling inline edit
  const handleCancelEdit = useCallback(() => {
    setEditingCell(null)
  }, [])

  // Handler for saving options inline edit (For Sale table only)
  const handleOptionsSave = useCallback(async (aircraftId: number, newOptions: string[]) => {
    try {
      const { supabase } = await import('../lib/supabase')

      const { data: existing } = await supabase
        .from('aircraft')
        .select('aircraft_id')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()

      if (existing) {
        const { error } = await supabase
          .from('aircraft')
          .update({ options: newOptions })
          .eq('aircraft_id', aircraftId)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('aircraft')
          .insert({ aircraft_id: aircraftId, options: newOptions })
        if (error) throw error
      }

      setEditingOptionsAircraftId(null)
      triggerOptionsRefresh(aircraftId)
    } catch (error) {
      throw error
    }
  }, [triggerOptionsRefresh])

  // Handler for canceling options inline edit
  const handleCancelOptionsEdit = useCallback(() => {
    setEditingOptionsAircraftId(null)
  }, [])

  // Handler for saving options inline edit for transaction overrides (Sold table)
  const handleTransactionOptionsSave = useCallback(async (transactionId: string, newOptions: string[]) => {
    try {
      await TransactionOverridesNewService.upsert({
        transaction_id: transactionId,
        options: newOptions
      })

      setTransactionOverrides(prev => {
        const newMap = new Map(prev)
        const existing = newMap.get(transactionId) || { transaction_id: transactionId } as TransactionOverride
        newMap.set(transactionId, { ...existing, options: newOptions })
        return newMap
      })

      setEditingOptionsAircraftId(null)
    } catch (error) {
      throw error
    }
  }, [setTransactionOverrides])

  return {
    editingCell,
    editingOptionsAircraftId,
    setEditingOptionsAircraftId,
    handleTakePriceSave,
    handleAskPriceSave,
    handleFieldSave,
    handleCompanySave,
    handleTransactionPriceSave,
    handleTransactionTakePriceSave,
    handleTransactionAskPriceSave,
    handleTransactionFieldSave,
    handleTransactionCompanySave,
    handleStartEdit,
    handleStartTransactionEdit,
    handleCancelEdit,
    handleOptionsSave,
    handleCancelOptionsEdit,
    handleTransactionOptionsSave,
  }
}

